import { AlertFactory, AlertState } from 'alerts/Alert'
import BillingMethod from 'billing/models/BillingMethod'
import TeamStore from 'iam/TeamStore'
import ModalViewModel from 'infra/ModalViewModel'

import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id, Nullable } from 'types/definitions'
import { roundToPrecision } from 'utils/number-util'
import { DEFAULT, match } from 'utils/pattern-match-util'
import { func, required, validationContext } from 'validx'

/**
 * Represents a billing method option.
 */
export type BillingMethodOption = {
  id: Id
  isDefault: boolean
  displayLabel: string
}

export class ChargeOrRefundDialogViewModel extends ModalViewModel {
  /**
   * The reason for the charge or refund.
   */
  @observable
  reason = ''

  /**
   * Billing method selected.
   */
  @observable
  billingMethod: Nullable<BillingMethodOption> = null

  /**
   * Amount to charge or refund.
   */
  @observable
  amount: number = 0

  /**
   * Mode of the dialog.
   */
  @observable
  mode: 'charge' | 'refund' = 'charge'

  /**
   * Validation context.
   */
  validation = validationContext(this, {
    reason: [required('Reason is required.')],
    amount: [
      required('Amount is required.'),
      func((opts) => opts.value > 0, 'Amount must be greater than 0.'),
    ],
  })

  constructor(
    private job: Job,
    private teamStore: TeamStore,
    private jobStore: JobStore,
    private getAvailableBillingMethods: () => BillingMethod[],
    private alertFactory: AlertFactory,
  ) {
    super()
    makeObservable(this)

    this.show$.subscribe(this.reset)
  }

  /**
   * Available billing methods.
   */
  @computed
  get availableBillingMethodOptions(): BillingMethodOption[] {
    return this.getAvailableBillingMethods().map(this.toBillingMethodOption)
  }

  /**
   * Disables the submit button.
   */
  @computed
  get disabled() {
    return match(this.mode, {
      charge: () => !this.billingMethod && !this.amount && !this.reason,
      refund: () => !this.amount && !this.reason,
      [DEFAULT]: () => false,
    })
  }

  /**
   * Submits the request for charging or refunding.
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const title = this.mode === 'charge' ? 'Charge' : 'Refund'

    const alert = this.alertFactory()
      .withState(AlertState.Pending)
      .saying(title, 'Hang tight!')
      .autoDismiss()
      .show()

    try {
      if (this.mode === 'charge') {
        await this.jobStore.chargeJob(
          this.job.id,
          this.billingMethod?.id,
          this.amount,
          this.reason,
        )
      } else {
        await this.jobStore.refundJob(this.job.id, this.amount, this.reason)
      }

      alert
        .withState(AlertState.Success)
        .saying('Success!', `${title} completed!`)
        .autoDismiss()
        .show()

      this.close()
    } catch (e) {
      alert
        .withState(AlertState.Error)
        .autoDismiss()
        .saying('Error!', `${title} for this job failed`)
        .show()

      throw e
    }
  })

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    const amount = roundToPrecision(this.job.total - this.job.paidAmount)

    // let's default to charge if the amount is zero or positive
    this.mode = amount >= 0 ? 'charge' : 'refund'

    this.setAmount(Math.abs(amount))
    this.setReason('')
    this.initializeBillingMethod()

    this.validation.reset()
    this.submit.reset()
  }

  /**
   * Sets the billing method.
   */
  @action.bound
  setBillingMethod(billingMethod: BillingMethodOption | undefined) {
    this.billingMethod = billingMethod ?? null
  }

  /**
   * Sets the amount.
   */
  @action.bound
  setAmount(amount: number) {
    this.amount = amount
  }

  /**
   * Sets the reason.
   */
  @action.bound
  setReason(reason: string) {
    this.reason = reason
  }

  /**
   * Sets the mode.
   */
  @action.bound
  setMode(mode: 'charge' | 'refund') {
    this.mode = mode
  }

  /**
   * Changes the mode and resets the amount, reason and billing method.
   */
  onChangeMode(mode: 'charge' | 'refund') {
    this.setReason('')
    this.setAmount(0)
    this.setMode(mode)

    if (mode === 'charge') {
      this.initializeBillingMethod()
    }

    this.validation.reset()
  }

  /**
   * Validates the view model.
   */
  validate() {
    this.validation.reset()

    if (!this.billingMethod && this.mode === 'charge') {
      this.validation.addErrors({
        billingMethod: ['Billing method is required.'],
      })

      return this.validation.validate().isValid
    }

    return this.validation.validate().isValid
  }

  /**
   * Converts a billing method to a billing method option.
   */
  @action.bound
  private toBillingMethodOption(x: BillingMethod) {
    const isDefault = x.id === x.billingProfile.defaultBillingMethodId

    const defaultLabel = isDefault ? '(default)' : ''
    const team = this.teamStore.teams.get(x.billingProfile.teamId)
    const creditTeamName = team?.name ?? 'Unknown'
    const teamName = team?.name ? `${team.name}:` : ''

    const displayLabel =
      x.type === 'Credit'
        ? `${creditTeamName} Team Credit`
        : `${teamName} ${x.brand} ending in ${x.last4} ${defaultLabel}`

    return {
      id: x.id,
      displayLabel,
      isDefault,
    }
  }

  /**
   * Initializes the billing method.
   */
  private initializeBillingMethod() {
    if (this.availableBillingMethodOptions.length > 0) {
      this.setBillingMethod(
        this.availableBillingMethodOptions.find((x) => x.isDefault) ??
          this.availableBillingMethodOptions[0],
      )
    }
  }
}
